import Phaser from 'phaser';
import { PlayerController } from './PlayerController';
import { AudioManager } from '../Systems/AudioManager';

export interface EnemyConfig {
  type: 'ground' | 'flying' | 'drone' | 'golem';
  x: number;
  y: number;
  patrolDistance?: number;
}

export class BaseEnemy extends Phaser.Physics.Arcade.Sprite {
  public enemyType: string;
  public startX: number;
  public patrolDist: number;
  public direction = 1; // 1 right, -1 left
  public isDefeated = false;
  private turnCooldown = 0;

  constructor(scene: Phaser.Scene, x: number, y: number, textureKey: string, type: string, patrolDist = 140) {
    super(scene, x, y, textureKey);
    this.enemyType = type;
    this.startX = x;
    this.patrolDist = patrolDist;

    scene.add.existing(this);
    scene.physics.add.existing(this);

    if (type === 'golem') {
      this.setScale(1.2);
    }
  }

  public updateEnemy(time: number, delta: number): void {
    if (this.isDefeated || !this.body) return;

    const body = this.body as Phaser.Physics.Arcade.Body;

    if (this.turnCooldown > 0) {
      this.turnCooldown -= delta;
    }

    if (this.enemyType === 'ground' || this.enemyType === 'golem') {
      const speed = this.enemyType === 'golem' ? 40 : 80;
      body.setVelocityX(this.direction * speed);

      // Check patrol bounds or wall collisions with turn cooldown to prevent rapid frame-toggle freezing
      const distX = Math.abs(this.x - this.startX);
      const isBlocked = body.blocked.left || body.blocked.right;
      if ((distX > this.patrolDist || isBlocked) && this.turnCooldown <= 0) {
        this.direction *= -1;
        this.setFlipX(this.direction > 0);
        this.turnCooldown = 300;
      }
    } else if (this.enemyType === 'flying') {
      body.setAllowGravity(false);
      body.setVelocityX(this.direction * 70);
      this.y += Math.sin(time * 0.005) * 1.2;

      const isBlocked = body.blocked.left || body.blocked.right;
      if ((Math.abs(this.x - this.startX) > this.patrolDist || isBlocked) && this.turnCooldown <= 0) {
        this.direction *= -1;
        this.turnCooldown = 300;
      }
    } else if (this.enemyType === 'drone') {
      body.setAllowGravity(false);
      body.setVelocityX(this.direction * 90);

      const isBlocked = body.blocked.left || body.blocked.right;
      if ((Math.abs(this.x - this.startX) > this.patrolDist || isBlocked) && this.turnCooldown <= 0) {
        this.direction *= -1;
        this.turnCooldown = 300;
      }
    }
  }

  public defeat(): void {
    if (this.isDefeated) return;
    this.isDefeated = true;
    AudioManager.playSFX('enemy_hit');

    // Death Squash Effect
    this.scene.tweens.add({
      targets: this,
      scaleY: 0.1,
      alpha: 0,
      duration: 200,
      onComplete: () => {
        this.destroy();
      },
    });
  }
}

export class EnemyManager {
  private scene: Phaser.Scene;
  public group: Phaser.Physics.Arcade.Group;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.group = scene.physics.add.group({
      runChildUpdate: false,
    });
  }

  public spawnEnemies(configs: EnemyConfig[]): void {
    configs.forEach((cfg) => {
      let textureKey = 'enemy_ground';
      if (cfg.type === 'flying') textureKey = 'enemy_flying';
      else if (cfg.type === 'drone') textureKey = 'enemy_drone';
      else if (cfg.type === 'golem') textureKey = 'enemy_golem';

      const enemy = new BaseEnemy(this.scene, cfg.x, cfg.y, textureKey, cfg.type, cfg.patrolDistance || 140);
      enemy.setDepth(30);
      this.group.add(enemy);
    });
  }

  public update(time: number, delta: number): void {
    this.group.getChildren().forEach((child) => {
      (child as BaseEnemy).updateEnemy(time, delta);
    });
  }

  public handlePlayerCollision(player: PlayerController, enemy: BaseEnemy): void {
    if (enemy.isDefeated) return;

    // Check if player landed on top of enemy (Stomp combat)
    const playerBottom = player.y + player.height / 2;
    const enemyTop = enemy.y - enemy.height / 2;

    if (player.body && player.body.velocity.y > 0 && playerBottom <= enemyTop + 16) {
      // Defeat enemy
      enemy.defeat();
      player.bounceUpward(-360);
    } else {
      // Player takes damage
      player.takeDamage(1);
    }
  }
}
